import { Rng, type SynthRequest, type SynthScore } from './compose';

export const METERS = {
  '4/4': { numerator: 4, denominator: 4, grouping: [2, 2], label: '4/4 · four beats' },
  '3/4': { numerator: 3, denominator: 4, grouping: [1, 1, 1], label: '3/4 · waltz' },
  '6/8': { numerator: 6, denominator: 8, grouping: [3, 3], label: '6/8 · two groups of three' },
  '5/4': { numerator: 5, denominator: 4, grouping: [3, 2], label: '5/4 · 3 + 2' },
  '7/8': { numerator: 7, denominator: 8, grouping: [2, 2, 3], label: '7/8 · 2 + 2 + 3' },
} as const;
export type MeterId = keyof typeof METERS;
export type MeterChoice = 'genre' | MeterId;
interface Genre {
  label: string; meter: MeterId; tempo: readonly [number, number];
  feel: 'electronic' | 'waltz' | 'jig' | 'asymmetric'; description: string;
}
/** Add a genre here, then supply any new rhythmic vocabulary in meterRhythm.
 * Tempo is quarter-note BPM; a jig at 144 has a dotted-quarter pulse of 96. */
export const GENRES = {
  synthwave: { label: 'Synthwave', meter: '4/4', tempo: [100, 116], feel: 'electronic', description: 'Steady kick, backbeat and answering synth phrases.' },
  waltz: { label: 'Chamber waltz', meter: '3/4', tempo: [100, 126], feel: 'waltz', description: 'Bass on one, light chords on two and three, turning melodic cells.' },
  jig: { label: 'Folk jig', meter: '6/8', tempo: [132, 156], feel: 'jig', description: 'Two dotted-quarter pulses, each divided into three eighth notes.' },
  'odd-groove': { label: 'Asymmetric groove', meter: '7/8', tempo: [102, 120], feel: 'asymmetric', description: 'Two short groups and a longer third group: 2 + 2 + 3.' },
} as const satisfies Record<string, Genre>;
/** 'place' = let the place choose (placeGenre); 'scene' = the scene's own style. */
export type GenreId = 'place' | 'scene' | keyof typeof GENRES;
/** 'scene' = the scene's own style (v4's original sound). */
export type PlaceGenre = 'scene' | keyof typeof GENRES;
const SCENE_GENRES: Record<SynthRequest['scene'], Partial<Record<PlaceGenre, number>>> = {
  'large-city': { synthwave: .45, scene: .35, 'odd-groove': .2 },
  'small-city': { scene: .4, waltz: .3, jig: .3 },
  exploration: { scene: 1 }, // a genre adds drums: exploration stays ambient
  flight: { scene: .55, synthwave: .25, 'odd-groove': .2 },
  fight: { scene: .6, 'odd-groove': .3, waltz: .1 }, // 3/4 in a fight: a danse macabre
};
const BIOME_BIAS: Record<SynthRequest['biome'], Partial<Record<PlaceGenre, number>>> = {
  terran: { jig: 1.4, synthwave: 1.2 }, ocean: { waltz: 1.7 }, ice: { waltz: 1.5, 'odd-groove': 1.2 },
  desert: { 'odd-groove': 1.7 }, volcanic: { 'odd-groove': 1.8, synthwave: .7 }, barren: { scene: 1.5 },
};
/** The genre a place plays: seeded by the world, weighted by its scene and biome.
 * Day and night share it (the same place keeps its music, just darker). */
export function placeGenre(req: SynthRequest): PlaceGenre {
  const weights = Object.entries(SCENE_GENRES[req.scene]).map(([g, w]) => [g, w! * (BIOME_BIAS[req.biome][g as PlaceGenre] ?? 1)] as const);
  let x = new Rng(req.seed, `genre|${req.biome}|${req.scene}`).next() * weights.reduce((s, [, w]) => s + w, 0);
  for (const [g, w] of weights) if ((x -= w) < 0) return g as PlaceGenre;
  return weights[0][0] as PlaceGenre;
}
export const barBeats = (score: Pick<SynthScore, 'meter'>): number => score.meter ? score.meter.numerator * 4 / score.meter.denominator : 4;
export function groupStarts(meter: MeterId): number[] {
  let at = 0;
  return METERS[meter].grouping.map(n => { const start = at; at += n * 4 / METERS[meter].denominator; return start; });
}
/** Native quarter-note offsets, not stretched copies of a 4/4 drum loop. */
export function meterRhythm(meter: MeterId) {
  switch (meter) {
    case '3/4': return { kicks: [0], snares: [2], replies: [1, 2], cells: [[0, 1, 1.5, 2], [0, .5, 1, 2], [0, 1.5, 2]] };
    case '6/8': return { kicks: [0, 1.5], snares: [1.5], replies: [1, 2.5], cells: [[0, .5, 1, 1.5, 2, 2.5], [0, 1, 1.5, 2.5], [0, .5, 1.5, 2, 2.5]] };
    case '5/4': return { kicks: [0, 3], snares: [1, 4], replies: [1.5, 2.5, 4.5], cells: [[0, 1, 2, 3, 3.5, 4], [0, .5, 2, 3, 4.5], [0, 1.5, 2, 3, 4]] };
    case '7/8': return { kicks: [0, 1, 2], snares: [2], replies: [.5, 1.5, 3], cells: [[0, .5, 1, 1.5, 2, 2.5, 3], [0, 1, 2, 3], [0, .5, 1, 2, 2.5]] };
    default: return { kicks: [0, 1, 2, 3], snares: [1, 3], replies: [.5, 2.5, 3.5], cells: [[0, .75, 1.5], [0, .5, 1.5], [0, 1], [.5, 1.25], [0, 1.5]] };
  }
}
